import { EditSpaceGroupFormV3 } from "@circle-react/components/SpaceGroups/EditFormV3";
import { DeleteSpaceGroupModal } from "@circle-react/components/SpaceGroups/Header/DeleteSpaceGroupModal";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useTailwindMdScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { Button } from "@circleco/compass/components/Button";
import { Modal } from "@circleco/compass/components/Modal";
import { Tabs } from "@circleco/compass/components/Tabs";
import { useState } from "react";

import { t } from "@/i18n-js/instance";

import {
  EDIT_SPACE_GROUP_MODAL_COMPASS_TAB,
  EDIT_SPACE_GROUP_MODAL_TAB,
  editSpaceGroupModalTabToCompassTab,
  type EditSpaceGroupModalCompassTab,
} from "./constants";
import { useEditSpaceGroupModal } from "./hooks/useEditSpaceGroupModal";
import { SpaceGroupMembersCompass } from "./SpaceGroupMembersCompass";
import {
  EDIT_SPACE_GROUP_FORM_ID,
  type EditSpaceGroupModalProps,
} from "./types";

interface EditSpaceGroupModalComponentProps extends EditSpaceGroupModalProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export const EditSpaceGroupModalComponent = ({
  spaceGroup: _spaceGroup,
  tab = EDIT_SPACE_GROUP_MODAL_TAB.DETAILS,
  open,
  onOpenChange,
}: EditSpaceGroupModalComponentProps) => {
  const [currentTab, setCurrentTab] = useState<EditSpaceGroupModalCompassTab>(
    editSpaceGroupModalTabToCompassTab(tab),
  );
  const isMdScreen = useTailwindMdScreenMediaQuery();

  const {
    activeSpaceGroupMembersCount,
    isEditing,
    isLoadingSpaceGroupResource,
    onSubmit,
    shouldShowConfirmDeleteModal,
    spaceGroup,
    toggleShouldShowConfirmDeleteModal,
  } = useEditSpaceGroupModal({
    spaceGroup: _spaceGroup,
    onClose: () => onOpenChange(false),
  });

  const isDetails = currentTab === EDIT_SPACE_GROUP_MODAL_COMPASS_TAB.DETAILS;
  const isMembers = currentTab === EDIT_SPACE_GROUP_MODAL_COMPASS_TAB.MEMBERS;

  const tabs = [
    {
      value: EDIT_SPACE_GROUP_MODAL_COMPASS_TAB.DETAILS,
      label: t("space_groups.details"),
    },
    {
      value: EDIT_SPACE_GROUP_MODAL_COMPASS_TAB.MEMBERS,
      label: t("space_groups.members"),
      badge:
        activeSpaceGroupMembersCount != null
          ? String(activeSpaceGroupMembersCount)
          : undefined,
    },
  ];

  return (
    <>
      <Modal.Root
        open={open}
        onOpenChange={onOpenChange}
        size="lg"
        position="top"
      >
        <Modal.Content>
          <Modal.Header title={t("space_groups.form.edit.title")} />
          <Modal.Body>
            <div
              id="scrollable-content"
              className={classNames("h-full", {
                "px-0 py-0": isMembers,
              })}
            >
              <Tabs.Root
                tabs={tabs}
                size="md"
                selectedValue={currentTab}
                onValueChange={value =>
                  setCurrentTab(value as EditSpaceGroupModalCompassTab)
                }
              >
                <div className="mt-4">
                  <Tabs.Panel
                    value={EDIT_SPACE_GROUP_MODAL_COMPASS_TAB.DETAILS}
                  >
                    <EditSpaceGroupFormV3
                      formId={EDIT_SPACE_GROUP_FORM_ID}
                      onSubmit={onSubmit}
                      defaultValues={spaceGroup}
                      loading={isLoadingSpaceGroupResource}
                    />
                  </Tabs.Panel>
                  <Tabs.Panel
                    value={EDIT_SPACE_GROUP_MODAL_COMPASS_TAB.MEMBERS}
                  >
                    <SpaceGroupMembersCompass spaceGroup={spaceGroup} />
                  </Tabs.Panel>
                </div>
              </Tabs.Root>
            </div>
          </Modal.Body>
          {isDetails && (
            <Modal.Footer
              secondaryAction={{
                label: t("cancel"),
                onClick: () => onOpenChange(false),
              }}
              primaryAction={{
                label: isEditing
                  ? t("space_groups.form.edit.submitting")
                  : t("save_changes"),
                type: "submit",
                form: EDIT_SPACE_GROUP_FORM_ID,
                loading: isEditing,
              }}
              startContent={
                isMdScreen ? (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={toggleShouldShowConfirmDeleteModal}
                  >
                    {t("space_groups.delete_space_group")}
                  </Button>
                ) : undefined
              }
            />
          )}
        </Modal.Content>
      </Modal.Root>
      <DeleteSpaceGroupModal
        spaceGroupId={spaceGroup.id}
        show={shouldShowConfirmDeleteModal}
        onClose={toggleShouldShowConfirmDeleteModal}
        onSuccess={() => onOpenChange(false)}
      />
    </>
  );
};
